/**
 * Horoscope contracts — the shape every layer agrees on for a daily / weekly /
 * monthly / yearly reading, in either tradition.
 *
 * Division of labour (house rule, do not blur it):
 *  - `@astrally/astro-engine` COMPUTES. It decides which configurations are
 *    active in a window and emits `Signal`s carrying the fact ids they came
 *    from. Everything a reading claims is traceable to engine output.
 *  - The AI only NARRATES an already-computed reading into `narration`. It
 *    never picks signals, never computes positions, never adds substance that
 *    is not already in `signals`.
 *  - This file is the wire/storage contract between them, and the cache key
 *    maths (`periodKeyFor`) both sides must agree on byte-for-byte.
 *
 * Cost rule: a reading's deterministic half (`headline`, `signals`, `byArea`)
 * must always render without any AI call. `narration` is the only paid part
 * and is filled in on an explicit user tap, then cached under `periodKey`.
 *
 * NOTE ON DUPLICATE NAMES: `@astrally/astro-engine` also exports
 * `HoroscopePeriod`, `LifeArea`, `LIFE_AREAS` and `Signal` (it defined them
 * locally before this contract layer existed). The definitions here are
 * structurally identical, so the two are freely interchangeable — but do not
 * import the same name from both packages in one file, that is a duplicate
 * identifier error. Prefer `@astrally/shared` outside the engine.
 */

import type { LocaleId } from './user.js';

// ---------------------------------------------------------------------------
// Axes
// ---------------------------------------------------------------------------

/**
 * Which tradition a reading is cast in. Also the value of the `data-mode`
 * attribute the UI sets to switch palettes (see the tradition palette rules in
 * app/src/styles/global.css).
 */
export type AstroSystem = 'vedic' | 'western';

export const ASTRO_SYSTEMS: AstroSystem[] = ['vedic', 'western'];

export type HoroscopePeriod = 'day' | 'week' | 'month' | 'year';

/** Stable display order, shortest window first. */
export const HOROSCOPE_PERIODS: HoroscopePeriod[] = ['day', 'week', 'month', 'year'];

/**
 * The eight buckets every signal falls into. Also the key order of `byArea`.
 * Identical to the engine's `LifeArea` — see the duplicate-names note above.
 */
export type LifeArea =
  | 'career'
  | 'money'
  | 'relationships'
  | 'health'
  | 'learning'
  | 'home'
  | 'travel'
  | 'inner';

export const LIFE_AREAS: LifeArea[] = [
  'career',
  'money',
  'relationships',
  'health',
  'learning',
  'home',
  'travel',
  'inner',
];

// ---------------------------------------------------------------------------
// Signals
// ---------------------------------------------------------------------------

/**
 * One thing the engine found in the window, already turned into plain English.
 *
 * Copy rule: `meaning` says what the placement MEANS inside the system — "a
 * lens on…", "the part of the week where…". Never what WILL happen.
 */
export interface Signal {
  /** Deterministic identity, e.g. "western.transit.saturn-square-venus". Also the de-dup key. */
  id: string;
  /** Short — reads as a chip or card heading. */
  label: string;
  /** One line of plain English, for a reader with zero astrology background. */
  meaning: string;
  /** 0..1 significance the engine assigned for this period. Sort key. */
  weight: number;
  area: LifeArea;
  /** Engine fact ids this signal is traceable to. NEVER empty. */
  factIds: string[];
  /** When the aspect perfects inside the window, if it does. */
  exactAtUtc?: string;
  /** Glossary term id when the label contains jargon (app/src/features/glossary/terms.ts). */
  term?: string;
}

// ---------------------------------------------------------------------------
// The stored reading
// ---------------------------------------------------------------------------

/**
 * A cached reading. `periodKey` is the document id and the cache key.
 *
 * `periodKey` alone does NOT distinguish the two traditions, so it is only
 * unique inside a per-system collection (or use `horoscopeCacheKey` for a flat
 * client cache). Read the format table on `periodKeyFor`.
 */
export interface HoroscopeReading {
  uid: string;
  system: AstroSystem;
  period: HoroscopePeriod;
  /** '2026-08-07' | '2026-W32' | '2026-08' | '2026' — see `periodKeyFor`. */
  periodKey: string;
  /** Deterministic one-liner. Rendered instantly, no AI involved. */
  headline: string;
  /** Retained signals, highest weight first. */
  signals: Signal[];
  /** The same signals bucketed. Every key is present; values may be empty. */
  byArea: Record<LifeArea, Signal[]>;
  /** AI narration of the signals above. Absent until the user asks for it. */
  narration?: string;
  /** Narration is language-specific — never serve one locale's text to another. */
  narrationLocale?: LocaleId;
  narratedAtISO?: string;
  generatedAtISO: string;
  /** Engine build that produced `signals`. Mismatch means recompute. */
  engineVersion: string;
}

/**
 * Flat cache key for client-side stores that hold both traditions together.
 * Firestore paths should instead nest by system and use `periodKey` as the id.
 */
export function horoscopeCacheKey(system: AstroSystem, periodKey: string): string {
  return `${system}:${periodKey}`;
}

// ---------------------------------------------------------------------------
// Period keys
// ---------------------------------------------------------------------------

const MS_PER_DAY = 86400000;

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;
const WEEK_KEY = /^(\d{4})-W(\d{2})$/;
const MONTH_KEY = /^(\d{4})-(\d{2})$/;
const YEAR_KEY = /^(\d{4})$/;

const dtfCache = new Map<string, Intl.DateTimeFormat>();

function getDtf(timeZone: string): Intl.DateTimeFormat {
  let dtf = dtfCache.get(timeZone);
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    dtfCache.set(timeZone, dtf);
  }
  return dtf;
}

interface CivilParts {
  year: number;
  /** 1-12 */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function civilPartsIn(utcMs: number, timeZone: string): CivilParts {
  const map: Record<string, number> = {};
  for (const p of getDtf(timeZone).formatToParts(new Date(utcMs))) {
    if (p.type !== 'literal') map[p.type] = Number(p.value);
  }
  return {
    year: map.year,
    month: map.month,
    day: map.day,
    // Some ICU builds render midnight as "24".
    hour: map.hour % 24,
    minute: map.minute,
    second: map.second,
  };
}

/** Offset (minutes east of UTC) `timeZone` applies at a UTC instant. */
function tzOffsetMinutes(utcMs: number, timeZone: string): number {
  const p = civilPartsIn(utcMs, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - utcMs) / 60000);
}

/**
 * Local civil midnight -> UTC ms, two-pass so DST transitions land correctly.
 * On the rare day a zone skips midnight entirely the result is the instant
 * just after the jump, which is what a "start of the local day" boundary wants.
 */
function zonedMidnightUtcMs(year: number, month: number, day: number, timeZone: string): number {
  const guess = Date.UTC(year, month - 1, day, 0, 0, 0);
  const off1 = tzOffsetMinutes(guess, timeZone);
  let utc = guess - off1 * 60000;
  const off2 = tzOffsetMinutes(utc, timeZone);
  if (off2 !== off1) utc = guess - off2 * 60000;
  return utc;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** ISO-8601 week-numbering year and week for a civil date. Weeks start Monday. */
function isoWeekOf(year: number, month: number, day: number): { isoYear: number; week: number } {
  const utc = Date.UTC(year, month - 1, day);
  // Monday = 0 .. Sunday = 6
  const dow = (new Date(utc).getUTCDay() + 6) % 7;
  const thursday = utc + (3 - dow) * MS_PER_DAY;
  const isoYear = new Date(thursday).getUTCFullYear();
  const jan1 = Date.UTC(isoYear, 0, 1);
  const week = Math.floor((thursday - jan1) / (7 * MS_PER_DAY)) + 1;
  return { isoYear, week };
}

/** Civil date of the Monday starting ISO week `week` of `isoYear`. */
function isoWeekMonday(isoYear: number, week: number): { year: number; month: number; day: number } {
  // 4 January is always in ISO week 1.
  const jan4 = Date.UTC(isoYear, 0, 4);
  const dow = (new Date(jan4).getUTCDay() + 6) % 7;
  const monday = new Date(jan4 - dow * MS_PER_DAY + (week - 1) * 7 * MS_PER_DAY);
  return { year: monday.getUTCFullYear(), month: monday.getUTCMonth() + 1, day: monday.getUTCDate() };
}

/**
 * The document id / cache key for the period containing `instant`, evaluated in
 * the reader's own timezone (never the server's, never UTC).
 *
 *   day    '2026-08-07'   local calendar date
 *   week   '2026-W32'     ISO-8601 week, Monday-start, ISO week-numbering year
 *   month  '2026-08'      local calendar month
 *   year   '2026'         local calendar year
 *
 * Both the client and Cloud Functions must call this rather than rolling their
 * own — a mismatch silently regenerates paid readings.
 *
 * @param instant Date or ISO string. Defaults to now.
 * @param timeZone IANA zone, e.g. "Asia/Kolkata".
 */
export function periodKeyFor(
  period: HoroscopePeriod,
  instant: Date | string = new Date(),
  timeZone = 'UTC',
): string {
  const ms = (typeof instant === 'string' ? new Date(instant) : instant).getTime();
  if (Number.isNaN(ms)) throw new Error(`periodKeyFor: invalid instant ${String(instant)}`);
  const p = civilPartsIn(ms, timeZone);
  switch (period) {
    case 'day':
      return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
    case 'week': {
      const { isoYear, week } = isoWeekOf(p.year, p.month, p.day);
      return `${isoYear}-W${pad2(week)}`;
    }
    case 'month':
      return `${p.year}-${pad2(p.month)}`;
    case 'year':
      return String(p.year);
  }
}

export interface PeriodKeyParts {
  period: HoroscopePeriod;
  /** For 'week' this is the ISO week-numbering year, which can differ from the calendar year. */
  year: number;
  /** 1-12. Month and day keys only. */
  month?: number;
  /** Day keys only. */
  day?: number;
  /** 1-53. Week keys only. */
  isoWeek?: number;
}

/**
 * Inverse of `periodKeyFor`: recovers the period type and its calendar parts.
 * Returns null for anything that is not a well-formed key, so callers can
 * reject junk document ids instead of trusting them.
 */
export function parsePeriodKey(periodKey: string): PeriodKeyParts | null {
  const day = DAY_KEY.exec(periodKey);
  if (day) {
    const year = Number(day[1]);
    const month = Number(day[2]);
    const dayOfMonth = Number(day[3]);
    if (month < 1 || month > 12 || dayOfMonth < 1 || dayOfMonth > 31) return null;
    return { period: 'day', year, month, day: dayOfMonth };
  }
  const week = WEEK_KEY.exec(periodKey);
  if (week) {
    const isoWeek = Number(week[2]);
    if (isoWeek < 1 || isoWeek > 53) return null;
    return { period: 'week', year: Number(week[1]), isoWeek };
  }
  const month = MONTH_KEY.exec(periodKey);
  if (month) {
    const m = Number(month[2]);
    if (m < 1 || m > 12) return null;
    return { period: 'month', year: Number(month[1]), month: m };
  }
  const year = YEAR_KEY.exec(periodKey);
  if (year) return { period: 'year', year: Number(year[1]) };
  return null;
}

export interface PeriodRange {
  /** Inclusive start of the window. */
  startISO: string;
  /** EXCLUSIVE end — the first instant of the next period. */
  endISO: string;
}

/**
 * The UTC window a period key covers in the reader's timezone. This is what the
 * engine's `fromUtc` / `toUtc` should be fed, so a stored reading always
 * describes exactly the span its key names.
 *
 * Throws on a malformed key — validate with `parsePeriodKey` first if the input
 * is untrusted.
 */
export function periodRangeUtc(periodKey: string, timeZone = 'UTC'): PeriodRange {
  const parts = parsePeriodKey(periodKey);
  if (!parts) throw new Error(`periodRangeUtc: malformed period key "${periodKey}"`);

  let start: { year: number; month: number; day: number };
  let end: { year: number; month: number; day: number };

  switch (parts.period) {
    case 'day': {
      start = { year: parts.year, month: parts.month as number, day: parts.day as number };
      const next = new Date(Date.UTC(start.year, start.month - 1, start.day) + MS_PER_DAY);
      end = { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate() };
      break;
    }
    case 'week': {
      start = isoWeekMonday(parts.year, parts.isoWeek as number);
      const next = new Date(Date.UTC(start.year, start.month - 1, start.day) + 7 * MS_PER_DAY);
      end = { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate() };
      break;
    }
    case 'month': {
      const m = parts.month as number;
      start = { year: parts.year, month: m, day: 1 };
      end = m === 12 ? { year: parts.year + 1, month: 1, day: 1 } : { year: parts.year, month: m + 1, day: 1 };
      break;
    }
    case 'year': {
      start = { year: parts.year, month: 1, day: 1 };
      end = { year: parts.year + 1, month: 1, day: 1 };
      break;
    }
  }

  return {
    startISO: new Date(zonedMidnightUtcMs(start.year, start.month, start.day, timeZone)).toISOString(),
    endISO: new Date(zonedMidnightUtcMs(end.year, end.month, end.day, timeZone)).toISOString(),
  };
}

/** True when `instant` still falls inside the period the key names. */
export function isPeriodKeyCurrent(
  periodKey: string,
  timeZone = 'UTC',
  instant: Date | string = new Date(),
): boolean {
  const parts = parsePeriodKey(periodKey);
  if (!parts) return false;
  return periodKeyFor(parts.period, instant, timeZone) === periodKey;
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

export function isAstroSystem(value: unknown): value is AstroSystem {
  return value === 'vedic' || value === 'western';
}

export function isHoroscopePeriod(value: unknown): value is HoroscopePeriod {
  return HOROSCOPE_PERIODS.includes(value as HoroscopePeriod);
}

export function isLifeArea(value: unknown): value is LifeArea {
  return LIFE_AREAS.includes(value as LifeArea);
}

/** An all-keys-present, all-empty `byArea` map. Use instead of `{} as Record<…>`. */
export function emptyByArea(): Record<LifeArea, Signal[]> {
  const out = {} as Record<LifeArea, Signal[]>;
  for (const area of LIFE_AREAS) out[area] = [];
  return out;
}
